import type { Value } from "platejs";

export type PageBackground = {
  type: "solid" | "gradient" | "split";
  color?: string;
  gradient?: {
    type: "linear" | "radial" | "conic";
    angle?: number;
    stops: Array<{ color: string; position: number }>;
    centerX?: number;
    centerY?: number;
    shape?: "circle" | "ellipse";
  };
  split?: {
    direction: "horizontal" | "vertical";
    position: number;
    first: { type: "solid" | "gradient"; color?: string; gradient?: PageBackground["gradient"] };
    second: { type: "solid" | "gradient"; color?: string; gradient?: PageBackground["gradient"] };
  };
};

export type PlatePage = {
  id: string;
  name: string;
  canvasSize: { width: number; height: number };
  background: PageBackground;
  plateValue: Value;
};

export type PlateEditorState = {
  version: 2;
  pages: PlatePage[];
  currentPageId: string;
  zoom: number;
  previewMode: boolean;
};

export type BlockSelectionInfo = {
  pageId: string;
  blockIndex: number;
  blockType: string;
};

export type RegenerateSectionRequest = {
  sectionTitle: string;
  sectionIndex: number;
  pageId: string;
  fullDocumentMarkdown: string;
};

export type ProposalPagesEditorConfig = {
  showToolbar?: boolean;
  showSidebar?: boolean;
  showSaveButton?: boolean;
  enableMultiPage?: boolean;
  enableVariables?: boolean;
  theme?: "light" | "dark";
  hideViewToggle?: boolean;
  viewMode?: "edit" | "preview";
};

export type PolicyFooterMeta = {
  policyName?: string;
  orgName?: string;
  date?: string;
  // Document classification / sensitivity label shown in the footer
  // (Public / Internal Use / Private / Confidential). Defaults to "Public"
  // when absent — set from the Q&A Tier 0 classification answer.
  classification?: string;
  // Optional free-text note appended after the classification line.
  note?: string;
};

export type ProposalPagesEditorProps = {
  config?: ProposalPagesEditorConfig;
  initialData?: Partial<PlateEditorState>;
  onChange?: (state: PlateEditorState) => void;
  onRegenerateSection?: (request: RegenerateSectionRequest) => void;
  isRegenerating?: boolean;
  className?: string;
  footerMeta?: PolicyFooterMeta;
  /** Inline footer edits bubble up through this callback (partial merge). */
  onFooterChange?: (updates: Partial<PolicyFooterMeta>) => void;
};

// A4 at 96 DPI
export const A4_WIDTH = 794;
export const A4_HEIGHT = 1123;

export const createEmptyPlateValue = (): Value => [
  { type: "p", children: [{ text: "" }] },
];

let pageIdCounter = 0;
export const newPageId = (): string =>
  `page-${Date.now().toString(36)}-${(pageIdCounter++).toString(36)}`;

export const createEmptyPage = (name = "Page 1"): PlatePage => ({
  id: newPageId(),
  name,
  canvasSize: { width: A4_WIDTH, height: A4_HEIGHT },
  background: { type: "solid", color: "#ffffff" },
  plateValue: createEmptyPlateValue(),
});

export const isPlateState = (state: unknown): state is PlateEditorState =>
  !!state &&
  typeof state === "object" &&
  (state as { version?: unknown }).version === 2;
